import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { FastForward, PlaneLanding, Loader2, Eye } from 'lucide-react';
import { CockpitOverlay } from './CockpitOverlay';
import { portal, Cinematic, IMG, countryImg } from './FlightScenes';
import { PHASE_DUR } from './sim/constants';

const FlightWorld = lazy(() => import('./sim/FlightWorld'));
const RouteGlobe = lazy(() => import('./sim/RouteGlobe'));

export const JET_FX = { citation: { kts: 488, clouds: 140 }, g550: { kts: 516, clouds: 190 }, global7500: { kts: 561, clouds: 245 } };

export const hasWebGL = (() => {
  let ok = null;
  return () => {
    if (ok === null) {
      try { const c = document.createElement('canvas'); ok = !!(c.getContext('webgl2') || c.getContext('webgl')); } catch { ok = false; }
    }
    return ok;
  };
})();

const AIRSPACE = { colombia: 'Caribbean', peru: 'Pacific', mexico: 'Gulf of Mexico' };

function labels(from, to, city) {
  const home = to === 'brooklyn';
  const CITY = city.toUpperCase();
  return {
    corridor: home ? 'Atlantic Return Corridor' : `${AIRSPACE[to] || 'Caribbean'} Air Corridor`,
    takeoff: home ? `${CITY} DEPARTURE · RETURN FLIGHT` : 'BROOKLYN DEPARTURE SEQUENCE',
    cruise: 'TRANSCONTINENTAL FLIGHT IN PROGRESS',
    landing: home ? 'APPROACHING JFK · BROOKLYN' : `APPROACHING ${CITY} RUNWAY`,
    cruiseLine: home ? 'Cruising at 41,000 FT over the dark Atlantic. JFK customs is waiting.' : `Cruising at 38,000 FT over ${AIRSPACE[to] || 'Caribbean'} international airspace.`,
    descend: home ? 'INITIATE DESCENT INTO JFK' : 'INITIATE DESTINATION DESCENT',
  };
}

function Readout({ phase, cruiseAlt, cruiseKts }) {
  const [t, setT] = useState(0);
  useEffect(() => { const s = performance.now(); const id = setInterval(() => setT((performance.now() - s) / 1000), 250); return () => clearInterval(id); }, [phase]);
  const alt = phase === 'cruise' ? cruiseAlt + Math.round(Math.sin(t * 0.4) * 40) : phase === 'takeoff' ? Math.max(0, Math.round((t - 5.2) * 900)) : Math.max(0, Math.round((6.5 - t) * 480));
  const spd = phase === 'cruise' ? cruiseKts : phase === 'takeoff' ? Math.min(180, Math.round(t * 25)) : Math.max(0, Math.round(t < 6.5 ? 140 : 140 - (t - 6.5) * 30));
  return (
    <div data-testid="flight-readout" className="flex gap-4 font-mono text-[11px] text-white/70">
      <span>ALT <b className="text-[#F3DC8A]">{alt.toLocaleString()}</b> FT</span><span>SPD <b className="text-[#F3DC8A]">{spd}</b> KTS</span>
    </div>
  );
}

function fallbackSteps(from, to, city, L) {
  return [
    { img: from === 'brooklyn' ? IMG.terminal : countryImg(from), kicker: L.takeoff, title: 'WHEELS UP', dur: 2400 },
    { img: IMG.cabin, kicker: L.cruise, title: 'CRUISING ALTITUDE', route: [from === 'brooklyn' ? 'BROOKLYN' : city.toUpperCase(), to === 'brooklyn' ? 'BROOKLYN' : city.toUpperCase()], dur: 2800 },
    { img: to === 'brooklyn' ? IMG.brooklyn : countryImg(to), kicker: L.landing, title: to === 'brooklyn' ? 'JFK' : city.toUpperCase(), dur: 2600 },
  ];
}

/** Live 3D flight: takeoff (auto) -> cruise (until player descends) -> landing (auto). Presentation only. */
export function FlightSequence({ from, to, city, onDone, jet = 'citation', jetName = 'Charter Citation', lat, lng }) {
  const [phase, setPhase] = useState('takeoff');
  const [view, setView] = useState('chase');
  const fx = JET_FX[jet] || JET_FX.citation;
  const alt = to === 'brooklyn' ? 41000 : 38000;
  const cockpit = phase === 'cruise' && view === 'cockpit';
  const done = useRef(false);
  const finish = () => { if (!done.current) { done.current = true; onDone(); } };
  const L = labels(from, to, city);
  if (!hasWebGL()) return <Cinematic steps={fallbackSteps(from, to, city, L)} onDone={onDone} />;
  const onPhaseEnd = (p) => (p === 'takeoff' ? setPhase('cruise') : finish());
  return portal(
    <div data-testid="flight-cinematic" data-step={phase} className="fixed inset-0 z-[3000] bg-black">
      <Suspense fallback={<div className="absolute inset-0 flex items-center justify-center text-white/50"><Loader2 className="animate-spin" /></div>}>
        <FlightWorld phase={phase} from={from} to={to} cruiseSky={to === 'brooklyn' ? 'night' : 'sunset'} onPhaseEnd={onPhaseEnd} livery={jet} view={view} cloudSpeed={fx.clouds} />
      </Suspense>
      {cockpit && <CockpitOverlay alt={alt} spd={fx.kts} />}
      <div key={phase} className="pointer-events-none absolute inset-0 bg-black fl-fadeout" />
      {phase === 'cruise' && lat != null && (
        <div className="absolute right-3 top-28 sm:right-6 sm:top-6 h-28 w-28 sm:h-44 sm:w-44 rounded-full border border-[#C9A84C]/30 bg-black/40 backdrop-blur-sm overflow-hidden shadow-[0_0_30px_rgba(201,168,76,0.15)] fl-rise">
          <Suspense fallback={null}><RouteGlobe lat={lat} lng={lng} homebound={to === 'brooklyn'} /></Suspense>
        </div>
      )}
      <div className="absolute left-3 right-3 sm:left-6 sm:right-auto top-3 sm:top-6 rounded-2xl border border-white/10 bg-black/60 backdrop-blur-md px-4 py-3 sm:px-5 sm:py-4 fl-rise" key={`hud-${phase}`}>
        <p data-testid="flight-phase-label" className="flex items-center gap-2 font-mono text-[10px] sm:text-[11px] tracking-[0.3em] text-[#C9A84C]"><span className="h-2 w-2 rounded-full bg-[#C9A84C] animate-pulse" />{L[phase]}</p>
        <p className="mt-1 text-sm sm:text-lg font-semibold text-white">{jetName} · {L.corridor}</p>
      </div>
      <div className="absolute inset-x-3 bottom-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[min(640px,90vw)] rounded-2xl border border-white/10 bg-black/70 backdrop-blur-md p-3 sm:p-4">
        {phase === 'cruise' ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2"><p className="text-xs sm:text-sm text-white/80">{L.cruiseLine}</p><Readout phase={phase} cruiseAlt={alt} cruiseKts={fx.kts} /></div>
            <div className="flex gap-2">
              <button type="button" data-testid="flight-descent-btn" onClick={() => setPhase('landing')}
                className="flex-1 h-12 rounded-full bg-gradient-to-b from-[#F3D36B] to-[#C9A84C] text-black font-bold tracking-widest text-xs sm:text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-[filter]">
                <PlaneLanding size={18} /> {L.descend}
              </button>
              <button type="button" data-testid="flight-view-toggle" onClick={() => setView((v) => (v === 'chase' ? 'cockpit' : 'chase'))}
                className="h-12 px-4 rounded-full border border-[#C9A84C]/50 text-[#F3D36B] text-xs flex items-center gap-1.5 hover:bg-[#C9A84C]/10 transition-colors"><Eye size={14} /> {view === 'chase' ? 'Cockpit' : 'Chase'}</button>
              <button type="button" data-testid="flight-skip-btn" onClick={finish} className="h-12 px-4 rounded-full border border-white/20 text-white/80 text-xs flex items-center gap-1.5 hover:border-[#C9A84C] transition-colors"><FastForward size={14} /> Skip</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden"><div key={phase} className="h-full bg-[#C9A84C] fl-progress" style={{ animationDuration: `${PHASE_DUR[phase]}s` }} /></div>
            <Readout phase={phase} cruiseAlt={alt} cruiseKts={fx.kts} />
            <button type="button" data-testid="flight-skip-btn" onClick={finish} className="h-9 px-3 rounded-full border border-white/20 bg-white/5 text-white/80 text-xs flex items-center gap-1.5 hover:border-[#C9A84C] transition-colors"><FastForward size={14} /> Skip</button>
          </div>
        )}
      </div>
    </div>
  );
}
